import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import Company from '../models/company.js';
import { renderDate, renderMoney } from '../helpers/format-render.js';
import { requestedDepthLabel } from '../ns-relationship/relationship-terms.js';

import './ns-company-card.js';
import './ns-detail-list.js';

export interface RelationshipSummaryParty {
  company: Company;
  role: string;
}

@customElement('ns-relationship-summary')
export class NSRelationshipSummary extends LitElement {
  @property({ type: String })
  heading = 'The relationship';

  @property({ attribute: false })
  parties: RelationshipSummaryParty[] = [];

  // Companies invited by email, which have no company record until they join
  @property({ attribute: false })
  invitedByEmail: string[] = [];

  @property({ attribute: false })
  contractValue?: number;

  @property({ type: String })
  contractCurrency = 'GBP';

  @property({ attribute: false })
  reviewDate?: Date | string;

  @property({ attribute: false })
  requestedDepth?: number | null;

  // Overrides the neutral depth wording where the reader's side is known
  @property({ type: String })
  depthLabel?: string;

  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      gap: var(--wa-space-m);

      padding: var(--wa-space-l);
      border-radius: var(--wa-border-radius-l);
      background: var(--secondary-background-color);
      color: var(--primary-card-text-color);
    }

    h3 {
      margin: 0;
      font-size: var(--wa-font-size-l);
      font-weight: normal;
    }

    .parties {
      display: flex;
      flex-direction: column;
      gap: var(--wa-space-xs);
    }

    ns-company-card {
      min-height: 56px;
    }

    .empty,
    .invited {
      margin: 0;
      font-size: var(--wa-font-size-s);
    }
  `;

  render() {
    return html`
      <h3>${this.heading}</h3>
      <div class="parties">
        ${this.parties.map(
          (party) => html`
            <ns-company-card .company=${party.company} subtitle=${party.role}></ns-company-card>
          `,
        )}
        ${this.invitedByEmail.map(
          (name) => html`
            <p class="invited">
              <strong>${name}</strong>
              · Supplier, invited by email
            </p>
          `,
        )}
        ${this.parties.length + this.invitedByEmail.length < 2
          ? html`
              <p class="empty">The companies you invite will appear here.</p>
            `
          : ''}
      </div>
      <ns-detail-list
        layout="grid"
        .fallback=${'Not set'}
        .rows=${[
          {
            label: 'Contract value',
            value:
              this.contractValue === undefined || this.contractValue === null
                ? undefined
                : renderMoney(this.contractValue, this.contractCurrency, { wholeUnits: true }),
          },
          { label: 'Review date', value: this.reviewDate ? renderDate(this.reviewDate, 'date') : undefined },
          { label: 'Requested depth', value: this.depthLabel ?? requestedDepthLabel(this.requestedDepth) },
        ]}
      ></ns-detail-list>
    `;
  }
}
